import { useMemo } from 'react';
import useSWR from 'swr';
import type { SWRConfiguration } from 'swr';
import type { AxiosRequestConfig } from 'axios';

import { fetcher, endpoints } from '@/lib/axios';
import { ENTREPRENEURSHIP_CATEGORY_SLUG } from '@/lib/constants';
import type {
  IFormation,
  IFormationsResponse,
  IRecommendedFormationsResponse,
} from '@/types/formation';

// ----------------------------------------------------------------------

const swrOptions: SWRConfiguration = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false,
};

// ----------------------------------------------------------------------

/**
 * Fetches the formations catalog, optionally scoped to one category.
 * Endpoint: GET /api/v1/formations[?category=slug][&exclude_category=slug]
 *
 * Entrepreneurship formations are only shown on the dedicated
 * /entrepreneurship page, so when browsing the whole catalog (no category
 * selected) that category is excluded server-side. Requesting it explicitly
 * (`useGetFormations(ENTREPRENEURSHIP_CATEGORY_SLUG)`) still works.
 */
export function useGetFormations(categorySlug?: string) {
  const url: string | [string, AxiosRequestConfig] =
    categorySlug && categorySlug !== 'all'
      ? [endpoints.formations.list, { params: { category: categorySlug } }]
      : [
          endpoints.formations.list,
          { params: { exclude_category: ENTREPRENEURSHIP_CATEGORY_SLUG } },
        ];

  const { data, isLoading, error, isValidating } = useSWR<IFormationsResponse>(
    url,
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      formations: (data?.data ?? []) as IFormation[],
      formationsLoading: isLoading,
      formationsError: error as Error | undefined,
      formationsValidating: isValidating,
      formationsEmpty: !isLoading && !(data?.data?.length),
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------

/** Ranking used by the homepage "Le top de nos formations" tabs. */
export type TopFormationsSort = 'top-rated' | 'most-requested';

/**
 * Fetches the top formations for a homepage showcase tab, ranked server-side.
 * Endpoint: GET /api/v1/formations?sort=<sort>&limit=<limit>
 *
 * Entrepreneurship formations are excluded here too — like the general catalog,
 * they only surface on the dedicated /entrepreneurship page.
 *
 * Pass `null` to skip the request entirely (e.g. while a different tab is
 * active), mirroring {@link useGetRecommendedFormations}.
 */
export function useGetTopFormations(sort: TopFormationsSort | null, limit = 5) {
  const url: [string, AxiosRequestConfig] | null = sort
    ? [
        endpoints.formations.list,
        { params: { sort, limit, exclude_category: ENTREPRENEURSHIP_CATEGORY_SLUG } },
      ]
    : null;

  const { data, isLoading, error, isValidating } = useSWR<IFormationsResponse>(
    url,
    fetcher,
    swrOptions
  );

  return useMemo(
    () => ({
      topFormations: (data?.data ?? []) as IFormation[],
      topFormationsLoading: isLoading,
      topFormationsError: error as Error | undefined,
      topFormationsValidating: isValidating,
      topFormationsEmpty: !isLoading && !(data?.data?.length),
    }),
    [data, error, isLoading, isValidating]
  );
}

// ----------------------------------------------------------------------

/**
 * Fetches other formations in the same category as the given formation.
 * Endpoint: GET /api/v1/formations/:slug/recommended
 */
export function useGetRecommendedFormations(slug: string) {
  const url = slug ? endpoints.formations.recommended(slug) : null;

  const { data, isLoading, error, isValidating } = useSWR<IRecommendedFormationsResponse>(
    url,
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      recommendedFormations: (data?.data ?? []) as IFormation[],
      recommendedFormationsLoading: isLoading,
      recommendedFormationsError: error as Error | undefined,
      recommendedFormationsValidating: isValidating,
      recommendedFormationsEmpty: !isLoading && !(data?.data?.length),
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}
